import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, X, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function MockedAckBanner() {
  const [status, setStatus] = useState(null);
  const [acking, setAcking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener("onboarding-status-changed", onChanged);
    return () => window.removeEventListener("onboarding-status-changed", onChanged);
  }, [load]);

  const ack = async () => {
    setAcking(true);
    try {
      await api.post("/onboarding/acknowledge-mocked-flags");
      toast.success("Acknowledged");
      await load();
      window.dispatchEvent(new Event("onboarding-status-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAcking(false);
    }
  };

  if (!status || status.mocked_flags_acknowledged) return null;

  return (
    <div
      className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 mb-6 flex items-start gap-3"
      data-testid="mocked-ack-banner"
    >
      <AlertCircle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm text-navy-800">
        <strong>Email is currently in MOCK mode</strong> — no real emails are being sent. To go live,
        save your Resend API key in{" "}
        <Link
          to="/super-admin/integrations"
          className="font-semibold text-orange-700 hover:underline inline-flex items-center gap-0.5"
          data-testid="mocked-ack-banner-go-live-link"
        >
          Super Admin → Integrations <ExternalLink className="w-3 h-3" />
        </Link>
        .
        <span className="block text-xs text-navy-500 mt-1">
          (Server env <code className="text-xs bg-white/60 px-1 rounded">RESEND_API_KEY</code> is also
          honoured as a fallback.)
        </span>
      </div>
      <button
        onClick={ack}
        disabled={acking}
        className="btn-accent text-xs px-3 py-1.5"
        data-testid="mocked-ack-banner-btn"
      >
        <X className="w-3.5 h-3.5" /> {acking ? "…" : "Got it"}
      </button>
    </div>
  );
}
